// ============================================
// CRM Types for Pipeline Management
// ============================================

// Deal type - which path the client is on
export type DealType = 'trial' | 'audit' | 'full';

// Activity types for engagement tracking
export type ActivityType = 'call' | 'email' | 'text' | 'meeting' | 'note';

// Access status for audit checklist
export type AccessStatus = 'pending' | 'requested' | 'granted' | 'na';

// Contract status for manual tracking (generic for any signing service)
export type ContractStatus = 'not_started' | 'draft' | 'sent' | 'viewed' | 'signed' | 'countersigned' | 'paid' | 'declined' | 'expired';

// ============================================
// Reference Data Types
// ============================================

export interface LeadSource {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
}

export interface AccessChecklistItem {
  id: string;
  name: string;
  category: string;
  sort_order: number;
  is_active: boolean;
}

// ============================================
// UI Helpers
// ============================================

export const ACTIVITY_TYPES: Record<ActivityType, { label: string; icon: string }> = {
  call: { label: 'Call', icon: 'Phone' },
  email: { label: 'Email', icon: 'Mail' },
  text: { label: 'Text', icon: 'MessageSquare' },
  meeting: { label: 'Meeting', icon: 'Calendar' },
  note: { label: 'Note', icon: 'FileText' },
};

export const ACCESS_STATUSES: Record<AccessStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'secondary' },
  requested: { label: 'Requested', color: 'warning' },
  granted: { label: 'Granted', color: 'success' },
  na: { label: 'N/A', color: 'muted' },
};

export const DEAL_TYPES: Record<DealType, { label: string; description: string }> = {
  trial: {
    label: 'Trial',
    description: 'Paid trial engagement with MSA'
  },
  audit: {
    label: 'Audit',
    description: 'Platform access and analysis'
  },
  full: {
    label: 'Full',
    description: 'Full service engagement'
  },
};

export const CONTRACT_STATUSES: Record<ContractStatus, { label: string; color: string; description: string }> = {
  not_started: {
    label: 'Not Started',
    color: 'secondary',
    description: 'Contract not yet created'
  },
  draft: {
    label: 'Draft',
    color: 'secondary',
    description: 'Contract being prepared'
  },
  sent: {
    label: 'Sent',
    color: 'warning',
    description: 'Sent to prospect for review'
  },
  viewed: {
    label: 'Viewed',
    color: 'info',
    description: 'Prospect has opened the contract'
  },
  signed: {
    label: 'Signed',
    color: 'success',
    description: 'Prospect has signed'
  },
  countersigned: {
    label: 'Countersigned',
    color: 'success',
    description: 'Both parties have signed'
  },
  paid: {
    label: 'Paid',
    color: 'success',
    description: 'Payment received'
  },
  declined: {
    label: 'Declined',
    color: 'destructive',
    description: 'Prospect declined to sign'
  },
  expired: {
    label: 'Expired',
    color: 'destructive',
    description: 'Contract expired without signature'
  },
};

// ============================================
// Unified Client Lifecycle Types
// ============================================

// Lifecycle stages for the unified client model
export type ClientLifecycleStage = 'lead' | 'opportunity' | 'interest' | 'deck_sent' | 'closing' | 'committed' | 'active' | 'churned' | 'paused';

// A churned or paused relationship is not a dead lead — it is someone worth
// re-approaching on a rhythm. Cadence drives the archive view's due list.
export type NurtureCadence = 'monthly' | 'quarterly' | 'semiannual' | 'annual';

// Revenue type for recurring vs variable/ad-hoc vs one-off clients
export type RevenueType = 'recurring' | 'variable' | 'one_off';

export const REVENUE_TYPES: Record<RevenueType, { label: string; description: string }> = {
  recurring: {
    label: 'Recurring',
    description: 'Fixed monthly retainer',
  },
  variable: {
    label: 'Variable',
    description: 'Ad-hoc payments per month',
  },
  one_off: {
    label: 'One-Off',
    description: 'Single payment — never counted in MRR',
  },
};

// Revenue types that contribute to MRR. 'variable' stays in because its dollars
// still recur; only the amount moves. 'one_off' never recurs, so it is excluded.
export function countsTowardMRR(revenueType?: RevenueType): boolean {
  return revenueType !== 'one_off';
}

// Churn reason enum
export type ChurnReason =
  | 'lost_to_competitor'
  | 'budget_constraints'
  | 'no_longer_needed'
  | 'poor_fit'
  | 'unresponsive'
  | 'project_completed'
  | 'other';

// Unified Client entity
export interface Client {
  id: string;

  // Core Identity
  company_name: string;
  primary_contact_name: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  website?: string;
  priority_tier?: number; // 1 = highest, 2 = medium, 3 = lower
  is_ghosty?: boolean; // Deal is too close to The Spacestation

  // Lifecycle
  lifecycle_stage: ClientLifecycleStage;
  stage_changed_at?: string;

  // Sales Context
  lead_source_id?: string;
  lead_source?: LeadSource;
  assigned_sales_owner_id?: string;
  assigned_sales_owner?: { id: string; name: string };
  deal_type?: DealType;
  estimated_monthly_value?: number;

  // Contract Context
  contract_status: ContractStatus;
  contract_document_url?: string;
  contract_signed_at?: string;
  expected_start_date?: string;

  // Project Context (for committed/active stages)
  actual_start_date?: string;
  strategy_sponsor_id?: string;
  strategy_sponsor?: { id: string; name: string };
  confirmed_monthly_revenue?: number;
  revenue_type?: RevenueType; // 'recurring' (default), 'variable' for ad-hoc payments, or 'one_off'
  profit_margin?: number;
  operations_overhead_percentage?: number;
  operations_compensation_per_owner?: number;

  // Churn/Pause Context
  churned_at?: string;
  churn_reason?: ChurnReason;
  churn_notes?: string;
  paused_at?: string;
  pause_reason?: string;
  expected_resume_date?: string;

  // Nurture (archive workspace) — churned/paused relationships worth revisiting
  nurture_cadence?: NurtureCadence;
  next_check_in_on?: string;

  // Legacy References
  legacy_prospect_id?: string;
  legacy_project_id?: string;

  // Linked Project (for active clients) - DEPRECATED
  linkedProject?: {
    id: string;
    name: string;
    status: string;
    legacy_id?: string;
  } | null;

  // Financial Junction Data (for active clients)
  operationsOwners?: string[]; // Array of owner names
  tacticalAssignments?: ClientTacticalAssignment[];
  performanceKickers?: ClientPerformanceKicker[];

  // Activity
  last_activity_at?: string;

  // Metadata
  created_at: string;
  updated_at: string;
  created_by?: string;
}

// Financial configuration types for clients
export interface ClientTacticalAssignment {
  id?: string;
  client_id?: string;
  specialist_id?: string;
  owner_id?: string;
  assigned_to_id?: string;
  owner?: { id: string; name: string };
  specialist?: { id: string; name: string };
  assigned_to?: { id: string; name: string }; // New: references team_members
  role_name: string;
  monthly_amount: number;
  handles_kickers: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ClientPerformanceKicker {
  id?: string;
  client_id?: string;
  name: string;
  marketing_spend: number;
  selected_tier_id?: string;
  tier?: { name: string };
  specialist_share_percentage: number;
  eligible_people: Array<{ name: string; type: string; role?: string }>;
  created_at?: string;
  updated_at?: string;
}

// Client Activity (engagement log)
export interface ClientActivity {
  id: string;
  client_id: string;
  activity_type: ActivityType;
  content: string;
  from_stage?: ClientLifecycleStage;
  to_stage?: ClientLifecycleStage;
  created_by?: string;
  created_at: string;
}

// Client Forecast (weighted pipeline and ad-hoc revenue)
export interface ClientForecast {
  id: string;
  client_id: string;
  forecast_month: string; // YYYY-MM
  forecasted_revenue?: number;
  ad_hoc_revenue?: number; // For variable-revenue clients: the actual amount for this month
  is_manual_override: boolean;
  override_reason?: string;
  created_at: string;
  updated_at: string;
}

// Client Access Status (for audits)
export interface ClientAccessStatus {
  id: string;
  client_id: string;
  checklist_item_id: string;
  checklist_item?: AccessChecklistItem;
  status: AccessStatus;
  granted_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// ============================================
// Client Input Types
// ============================================

export interface ClientInput {
  company_name: string;
  primary_contact_name: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  website?: string;
  priority_tier?: number;
  is_ghosty?: boolean;
  lifecycle_stage?: ClientLifecycleStage;
  lead_source_id?: string;
  assigned_sales_owner_id?: string;
  deal_type?: DealType;
  estimated_monthly_value?: number;
  contract_status?: ContractStatus;
  contract_document_url?: string;
  expected_start_date?: string;
  strategy_sponsor_id?: string;
  confirmed_monthly_revenue?: number;
  revenue_type?: RevenueType;
  profit_margin?: number;
  nurture_cadence?: NurtureCadence | null;
  next_check_in_on?: string | null;
}

// For transitioning to a new lifecycle stage
export interface ClientTransitionInput {
  to_stage: ClientLifecycleStage;
  // Fields required for specific transitions
  contract_signed_at?: string;
  expected_start_date?: string;
  actual_start_date?: string;
  confirmed_monthly_revenue?: number;
  profit_margin?: number;
  strategy_sponsor_id?: string;
  churn_reason?: ChurnReason;
  churn_notes?: string;
  pause_reason?: string;
  expected_resume_date?: string;
}

// ============================================
// Client Lifecycle UI Helpers
// ============================================

export const LIFECYCLE_STAGES: Record<ClientLifecycleStage, {
  label: string;
  color: string;
  description: string;
}> = {
  lead: {
    label: 'Lead',
    color: 'secondary',
    description: 'Initial contact made',
  },
  opportunity: {
    label: 'Opportunity',
    color: 'warning',
    description: 'Active sales engagement',
  },
  interest: {
    label: 'Intro Made',
    color: 'info',
    description: 'Introductory call completed',
  },
  deck_sent: {
    label: 'Deck Sent',
    color: 'info',
    description: 'Pitch deck sent, awaiting feedback',
  },
  closing: {
    label: 'Pricing Sent',
    color: 'warning',
    description: 'Pricing proposal sent, finalizing terms',
  },
  committed: {
    label: 'Signed',
    color: 'accent',
    description: 'Contract signed, awaiting start',
  },
  active: {
    label: 'Active',
    color: 'success',
    description: 'Live project generating revenue',
  },
  churned: {
    label: 'Churned',
    color: 'destructive',
    description: 'Lost or canceled',
  },
  paused: {
    label: 'Paused',
    color: 'muted',
    description: 'Temporarily inactive',
  },
};

export const CHURN_REASONS: Record<ChurnReason, { label: string }> = {
  lost_to_competitor: { label: 'Lost to Competitor' },
  budget_constraints: { label: 'Budget Constraints' },
  no_longer_needed: { label: 'No Longer Needed' },
  poor_fit: { label: 'Poor Fit' },
  unresponsive: { label: 'Unresponsive' },
  project_completed: { label: 'Project Completed' },
  other: { label: 'Other' },
};

export const PRIORITY_TIERS: Record<number, { label: string; color: string; description: string }> = {
  1: {
    label: 'Tier 1',
    color: 'destructive',
    description: 'Highest priority prospects',
  },
  2: {
    label: 'Tier 2',
    color: 'warning',
    description: 'Medium priority prospects',
  },
  3: {
    label: 'Tier 3',
    color: 'secondary',
    description: 'Lower priority prospects',
  },
};

// All pipeline stages (excludes churned/paused which are off-board)
const ALL_PIPELINE_STAGES: ClientLifecycleStage[] = ['lead', 'opportunity', 'interest', 'deck_sent', 'closing', 'committed', 'active'];

// The two stages that live in the archive workspace rather than on the board.
export const ARCHIVED_STAGES: ClientLifecycleStage[] = ['churned', 'paused'];

export function isArchived(client: { lifecycle_stage: ClientLifecycleStage }): boolean {
  return ARCHIVED_STAGES.includes(client.lifecycle_stage);
}

export const NURTURE_CADENCES: Record<NurtureCadence, { label: string; months: number }> = {
  monthly: { label: 'Monthly', months: 1 },
  quarterly: { label: 'Quarterly', months: 3 },
  semiannual: { label: 'Every 6 months', months: 6 },
  annual: { label: 'Yearly', months: 12 },
};

// Next check-in = today plus the cadence. Returned as a yyyy-mm-dd date string,
// built in local time so "today" means Ken's today, not UTC's.
export function nextCheckInFor(cadence: NurtureCadence, from: Date = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setMonth(d.getMonth() + NURTURE_CADENCES[cadence].months);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function isCheckInDue(client: { next_check_in_on?: string }): boolean {
  if (!client.next_check_in_on) return false;
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return client.next_check_in_on <= today;
}

// Get allowed transitions from a given stage — any stage can move to any other stage
export function getAllowedTransitions(currentStage: ClientLifecycleStage): ClientLifecycleStage[] {
  const allStages: ClientLifecycleStage[] = [...ALL_PIPELINE_STAGES, 'churned', 'paused'];
  return allStages.filter(s => s !== currentStage);
}

// ============================================
// Pipeline Summary (Unified)
// ============================================

export interface ClientPipelineSummary {
  stage: ClientLifecycleStage;
  client_count: number;
  total_estimated_value: number;
  total_confirmed_revenue: number;
  total_revenue: number;
}
